import { requestJson } from "../api/apiClient";

/**
 * @typedef {Object} DealerVehicleListingIdentityMetadata
 * @property {string} marketListingId - Explicit `vehicle_market_listings.id` (equals dealer response `id`).
 * @property {string|null} mobilityListingId - Linked `vehicle_listing.id` when exactly one valid mapping exists.
 * @property {string|null} canonicalVehicleId - Cross-marketplace grouping id (`canonical_vehicle.id`).
 * @property {string|null} canonicalPublicUrl - Canonical public route using mobility listing id.
 */

const PORTAL_BASE = "/api/v1/automap/dealer-portal";
const ADMIN_BASE = "/api/admin/automotive";

export function fetchDealerPortalDealers() {
  return requestJson(`${PORTAL_BASE}/dealers`);
}

export function fetchDealerInventory(dealerId, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings${qs ? `?${qs}` : ""}`);
}

export function fetchDealerInventoryQuality(dealerId) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings/quality`);
}

export function fetchDealerListing(dealerId, listingId) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}`);
}

export function createDealerListing(dealerId, payload) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateDealerListing(dealerId, listingId, payload) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function runDealerListingAction(dealerId, listingId, action, payload = {}) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}/actions`, {
    method: "POST",
    body: JSON.stringify({ action, ...payload }),
  });
}

export function duplicateDealerListing(dealerId, listingId) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}/duplicate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

function buildQs(params) {
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return "";
  return `?${new URLSearchParams(Object.fromEntries(entries)).toString()}`;
}

export function fetchAdminListings(params = {}) {
  return requestJson(`${ADMIN_BASE}/listings${buildQs(params)}`);
}

export function fetchAdminListingReviewQueue(params = {}) {
  return requestJson(`${ADMIN_BASE}/listings/review${buildQs(params)}`);
}

export function fetchAdminListingStaleQueue(params = {}) {
  return requestJson(`${ADMIN_BASE}/listings/stale${buildQs(params)}`);
}

export function fetchAdminListingMissingPhotosQueue(params = {}) {
  return requestJson(`${ADMIN_BASE}/listings/missing-photos${buildQs(params)}`);
}

export function fetchAdminListingDuplicatesQueue(params = {}) {
  return requestJson(`${ADMIN_BASE}/listings/duplicates${buildQs(params)}`);
}

export function fetchAdminListingOutliersQueue(params = {}) {
  return requestJson(`${ADMIN_BASE}/listings/outliers${buildQs(params)}`);
}

export function fetchAdminListing(listingId) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}`);
}

export function fetchAdminListingDraftReview(listingId) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}/draft-review`);
}

export function updateAdminListingDraftReview(listingId, payload = {}) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}/draft-review`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function updateAdminListing(listingId, payload) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function runAdminListingAction(listingId, action, payload = {}) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}/actions`, {
    method: "POST",
    body: JSON.stringify({ action, ...payload }),
  });
}

export function runAdminListingBulkAction(listingIds, action, payload = {}) {
  return requestJson(`${ADMIN_BASE}/listings/bulk-actions`, {
    method: "POST",
    body: JSON.stringify({ listingIds, action, ...payload }),
  });
}

export function createAdminListingNote(listingId, payload) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}/notes`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function flagAdminListingDuplicate(listingId, payload) {
  return requestJson(`${ADMIN_BASE}/listings/${encodeURIComponent(listingId)}/duplicate-flag`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function bulkConfirmDealerListings(dealerId, listingIds) {
  return requestJson(`${PORTAL_BASE}/dealers/${encodeURIComponent(dealerId)}/listings/bulk-confirm`, {
    method: "POST",
    body: JSON.stringify({ listingIds }),
  });
}

export function trackInventoryEvent(eventType, metadata = {}) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.debug("[inventory-event]", eventType, metadata);
  }
}
